import { useState, useCallback, useMemo } from 'react';
import { colord, extend } from 'colord';
import cmykPlugin from 'colord/plugins/cmyk';

extend([cmykPlugin]);

// Simple debounce implementation
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export interface RGBAColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface CMYKColor {
  c: number;
  m: number;
  y: number;
  k: number;
}

export function useColorPicker(initialColor: string) {
  const [color, setColor] = useState<RGBAColor>(() => {
    const c = colord(initialColor);
    return c.toRgb();
  });

  const [cmykValues, setCmykValues] = useState<CMYKColor>(() => {
    const cmyk = colord(color).toCmyk();
    return {
      c: Math.round(cmyk.c * 100),
      m: Math.round(cmyk.m * 100),
      y: Math.round(cmyk.y * 100),
      k: Math.round(cmyk.k * 100),
    };
  });

  // Convert RGB to CMYK
  const rgbToCmyk = useCallback((color: RGBAColor) => {
    const cmyk = colord(color).toCmyk();
    return {
      c: Math.round(cmyk.c * 100),
      m: Math.round(cmyk.m * 100),
      y: Math.round(cmyk.y * 100),
      k: Math.round(cmyk.k * 100),
    };
  }, []);

  const debouncedColorChange = useMemo(
    () => (newColor: RGBAColor) => {
      setColor(newColor);
      setCmykValues(rgbToCmyk(newColor));
      return colord(newColor).toHex();
    },
    [rgbToCmyk]
  );

  // Update RGB color and sync CMYK values
  const handleColorChange = useCallback((newColor: RGBAColor) => {
    setColor(newColor);
    setCmykValues(rgbToCmyk(newColor));
    return colord(newColor).toHex();
  }, [rgbToCmyk]);

  // Update a single CMYK value and sync RGB
  const handleCMYKChange = useCallback((key: keyof CMYKColor, value: number) => {
    const newCmyk = { ...cmykValues, [key]: value };
    setCmykValues(newCmyk);
  
    // Convert CMYK to RGB
    const newRgb = colord({
      c: newCmyk.c / 100,
      m: newCmyk.m / 100,
      y: newCmyk.y / 100,
      k: newCmyk.k / 100,
    }).toRgb();
  
    // Preserve alpha
    const newColor = { ...newRgb, a: color.a };
    setColor(newColor);
  
    return colord(newColor).toHex();
  }, [cmykValues, color.a]);

  return {
    color,
    cmykValues,
    handleColorChange: debouncedColorChange,
    handleCMYKChange,
    getRgbaString: () => `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`,
    getHexString: () => colord(color).toHex(),
  };
} 